import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * IdleLogout : déconnexion automatique après 30 minutes d'inactivité
 * Affiche une modale d'avertissement 5 minutes avant la déconnexion.
 *
 * S'active uniquement quand un utilisateur est connecté (admin OU official).
 * Au logout : redirection vers /login (ou /officiel/login) avec `?expired=1`
 * pour que la page d'authentification affiche un message clair.
 */

// Durées configurables (centralisées pour faciliter les tests / changements)
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 min
const WARNING_BEFORE_MS = 5 * 60 * 1000; // 5 min avant le logout

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "wheel"];

export default function IdleLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSec, setRemainingSec] = useState(WARNING_BEFORE_MS / 1000);
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const clearAllTimers = () => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownTimerRef.current = null;
  };

  const performLogout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    const wasOfficial = user?.role === "official";
    try {
      await logout();
    } catch {
      // best-effort
    }
    const target = wasOfficial ? "/officiel/login?expired=1" : "/login?expired=1";
    navigate(target, { replace: true });
  }, [logout, navigate, user]);

  const scheduleTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSec(Math.floor(WARNING_BEFORE_MS / 1000));
      // Compte à rebours visible dans la modale
      countdownTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const remainMs = IDLE_TIMEOUT_MS - elapsed;
        setRemainingSec(Math.max(0, Math.floor(remainMs / 1000)));
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
    logoutTimerRef.current = setTimeout(performLogout, IDLE_TIMEOUT_MS);
  }, [performLogout]);

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    // Si la modale d'avertissement est ouverte, on n'auto-reset PAS — l'utilisateur
    // doit explicitement cliquer "Rester connecté" pour confirmer sa présence.
    if (showWarning) return;
    scheduleTimers();
  }, [scheduleTimers, showWarning]);

  // (Re)démarre les timers à chaque login / navigation utilisateur
  useEffect(() => {
    if (!user) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }
    scheduleTimers();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, handleActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handleActivity));
      clearAllTimers();
    };
  }, [user, scheduleTimers, handleActivity]);

  const stayConnected = () => {
    lastActivityRef.current = Date.now();
    scheduleTimers();
  };

  if (!user || !showWarning) return null;

  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="idle-warning-modal"
    >
      <div
        className="rounded-2xl max-w-md w-full p-6"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
      >
        <div className="flex items-start gap-3 mb-4">
          <span
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "var(--surface-3)", border: "1px solid var(--border-strong)" }}
          >
            <Clock size={16} strokeWidth={2.4} />
          </span>
          <div className="min-w-0">
            <div className="label mb-1">Session inactive</div>
            <h3 className="font-serif text-2xl leading-tight">Tu seras déconnecté bientôt</h3>
          </div>
        </div>
        <p className="text-sm text-[var(--text)] mb-3 leading-relaxed">
          Aucune activité détectée depuis 25 minutes. Pour ta sécurité, tu seras automatiquement
          déconnecté dans&nbsp;:
        </p>
        <div
          className="rounded-xl p-4 mb-4 text-center"
          style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}
        >
          <div className="tabular text-4xl font-bold" data-testid="idle-countdown">
            {mm}:{ss}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-1">
            minutes restantes
          </div>
        </div>
        <div className="flex flex-col sm:flex-row-reverse gap-2">
          <button
            type="button"
            onClick={stayConnected}
            className="btn-primary inline-flex items-center justify-center gap-1.5"
            data-testid="idle-stay-btn"
          >
            <Clock size={14} strokeWidth={2.4} /> Rester connecté
          </button>
          <button
            type="button"
            onClick={performLogout}
            className="btn-ghost inline-flex items-center justify-center gap-1.5"
            data-testid="idle-logout-btn"
          >
            <LogOut size={14} strokeWidth={2.4} /> Se déconnecter maintenant
          </button>
        </div>
      </div>
    </div>
  );
}
